// src/components/CarCard.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaHeart, FaRegHeart } from "react-icons/fa6";

export default function CarCard({
  car,
  isCompared = false,
  onCompareToggle = () => {},
  ...rest
}) {
  const {
    base: { color, mileage, modelDescription, price, thumbnail, status } = {},
    extended: {
      make = "",
      model = "",
      extColor = "",
      fuel = "",
      year,
      extModel = "",
    } = {},
  } = car;

  const isSold = status === "sold";



  // PCP Calculation (48 months, 8,000 miles)
  const pcpTerm = 48;
  const pcpMileage = 8000;
  const pcpDeposit = price ? price * 0.1 : 0;
  const pcpAprByTerm = {
    24: 0.119,
    36: 0.085,
    42: 0.085,
    48: 0.085,
    40: 0.085,
  };
  const pcpBalloons = {
    24: { 8000: 0.50, 10000: 0.475, 12000: 0.45, 15000: 0.425 },
    36: { 8000: 0.525, 10000: 0.50, 12000: 0.475, 15000: 0.45 },
    42: { 8000: 0.475, 10000: 0.45, 12000: 0.425, 15000: 0.40 },
    48: { 8000: 0.425, 10000: 0.40, 12000: 0.375, 15000: 0.35 },
    40: { 8000: 0.45 },
  };
  let pcpMonthly = 0;
  if (price && pcpBalloons[pcpTerm] && pcpBalloons[pcpTerm][pcpMileage]) {
    const APR = pcpAprByTerm[pcpTerm] || 0.085;
    const r = APR / 12;
    const balloonValue = price * pcpBalloons[pcpTerm][pcpMileage];
    const pv = balloonValue / Math.pow(1 + r, pcpTerm);
    pcpMonthly = ((price - pcpDeposit - pv) * r) / (1 - Math.pow(1 + r, -pcpTerm));
    if (isNaN(pcpMonthly)) pcpMonthly = 0;
  }

  // purely local toggle
  const [favourited, setFavourited] = useState(false);
  const handleFavouriteClick = () => {
    setFavourited((prev) => !prev);
  };

  const [imgError, setImgError] = useState(false);

  return (
    <div
      {...rest}
      className="group flex flex-col h-full border border-[#B2B2B2] overflow-hidden bg-white text-primary shadow hover:shadow-lg transition"
    >
      <div className="flex-grow">
        <Link to={`/car/${car.base.id}`} className="block">
          {/* Image */}

          <div className="relative w-full aspect-[4/3] overflow-hidden">
            {thumbnail && !imgError && (
              <img
                src={thumbnail}
                alt={`${make} ${extModel || model}`}
                onError={() => setImgError(true)}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
              />
            )}
            {/* {thumbnail && !imgError && !isSold ? (
              <img
                src={thumbnail}
                alt={`${make} ${extModel || model}`}
                onError={() => setImgError(true)}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
              />
            ) : (
              <img
                src="https://zorqkblobstorage.blob.core.windows.net/zorqkblobstorage/Zorqk-coming-soon.jpg" // fallback/sold image
                alt={isSold ? "Vehicle Sold" : "Default car"}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
              />
            )} */}

            {/* SOLD Badge Overlay */}
            {/* {isSold && (
              <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                <div className="bg-red-600 text-white px-8 py-4 rounded-lg font-bold text-2xl transform -rotate-12 shadow-lg">
                  SOLD
                </div>
              </div>
            )} */}
          </div>

          <div className="p-4 flex flex-col h-full">
            {/* Title */}
            <h3 className="text-lg font-semibold text-primary">
              {make} {extModel || model}
            </h3>
            {/* Model description */}
            <p className="text-sm text-secondary mb-3">{modelDescription}</p>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 font-medium text-black text-xs">
              {year && (
                <span className="px-4 py-1 bg-customGray rounded-full">
                  {year}
                </span>
              )}
              {mileage != null && (
                <span className="px-4 py-1 bg-customGray rounded-full">
                  {mileage.toLocaleString()} miles
                </span>
              )}
              {fuel && (
                <span className="px-4 py-1 bg-customGray rounded-full">
                  {fuel}
                </span>
              )}
              {color && (
                <span className="px-4 py-1 bg-customGray rounded-full">
                  {extColor || color}
                </span>
              )}
            </div>
          </div>
        </Link>
      </div>

      {/* Pricing: PCP only */}
      <div className="flex flex-col gap-1 px-4 py-4">
        <div className="flex flex-row justify-between w-full">
          <span className="text-xl font-bold text-primary">
            {price != null ? `£${price.toLocaleString()}` : "—"}
          </span>
          <span className="flex flex-col items-end text-primary">
            <span className="text-xl font-bold">
              {pcpMonthly ? `£${Number(pcpMonthly).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : "—"}
            </span>
            <span className="text-sm leading-tight">48 month PCP</span>
          </span>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="p-4 border-t border-secondaryAccent flex flex-wrap gap-2 justify-between items-center">
        <div className="flex gap-2">
          <label
            className={`inline-flex items-center space-x-1 text-sm font-bold ${
              isSold
                ? "text-gray-400 cursor-not-allowed"
                : "text-secondary hover:text-accent"
            }`}
          >
            <input
              type="checkbox"
              checked={isCompared}
              onChange={onCompareToggle}
              disabled={isSold}
              className="h-4 w-4 text-primary border-secondaryAccent rounded disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span>Compare</span>
          </label>

          <button
            onClick={handleFavouriteClick}
            disabled={isSold}
            className={`inline-flex items-center space-x-1 text-sm font-bold transition ${
              isSold
                ? "text-gray-400 cursor-not-allowed"
                : "text-secondary hover:text-accent"
            }`}
          >
            {favourited ? (
              <FaHeart className="h-5 w-5 text-red-500" />
            ) : (
              <FaRegHeart className="h-5 w-5 text-secondary-accent" />
            )}
            <span>{favourited ? "Saved" : "Favourite"}</span>
          </button>
        </div>
        {/* Finance button removed as requested */}
      </div>
    </div>
  );
}
